import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { BookingObjectType } from 'src/components/BookingObjectTypeSelect/types';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface MyBookingsTypeContextValue {
  type: BookingObjectType | undefined;
  setType: (type: BookingObjectType) => void;
}

export const MyBookingsTypeContext = createContext<
  MyBookingsTypeContextValue | undefined
>(undefined);
MyBookingsTypeContext.displayName = 'MyBookingsTypeContext';

interface MyBookingsTypeProviderProps {
  children?: ReactNode;
}

export const MyBookingsTypeProvider: FC<MyBookingsTypeProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = (searchParams.get('type') ?? undefined) as
    | BookingObjectType
    | undefined;

  const setType = useCallback(
    (value: BookingObjectType) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('type', value);
        return next;
      });
      dispatch(sidesheetActions.close());
    },
    [dispatch, setSearchParams],
  );

  useEffect(() => {
    if (!type) {
      setType(BookingObjectType.Desk);
    }
  }, [type, setType]);

  const contextValue = useMemo((): MyBookingsTypeContextValue => {
    return {
      type: type,
      setType: setType,
    };
  }, [type, setType]);

  return (
    <MyBookingsTypeContext.Provider value={contextValue}>
      {children}
    </MyBookingsTypeContext.Provider>
  );
};

export const useMyBookingsType = (): MyBookingsTypeContextValue =>
  useRequiredContext(MyBookingsTypeContext);
